import React from "react";
import SmsLegalShell from "./SmsLegalShell";

/**
 * SMS / 10DLC Terms & Conditions for Life Foundation
 * Public URL: /sms/terms
 *
 * ⚠️ TWILIO / TCR COMPLIANCE — DO NOT REMOVE THE FOLLOWING ELEMENTS:
 *   • Program name (Section 1)
 *   • Program description (Section 1)
 *   • "Message and data rates may apply" — Section 3 (exact wording
 *     required by carriers)
 *   • Message frequency disclosure — Section 3
 *   • STOP opt-out language in BOLD — Section 4
 *   • HELP support language in BOLD — Section 4
 *   • Support contact info — Section 4
 *   • Carrier non-liability disclaimer — Section 6
 *
 * These items are checked by The Campaign Registry (TCR) during A2P
 * 10DLC campaign vetting. Removing any of them will cause the campaign
 * to be rejected and SMS delivery to fail.
 */
export default function SmsTerms() {
  const lastUpdated = "February 12, 2026";

  return (
    <SmsLegalShell
      testId="sms-terms-page"
      eyebrow="SMS Program · Terms"
      title="SMS Terms &amp; Conditions"
      lastUpdated={lastUpdated}
    >
      <p>
        These SMS Terms &amp; Conditions (the “SMS Terms”) govern your
        participation in the text‑messaging program operated by{" "}
        <strong>Life Foundation</strong> (EIN 99‑3187855), a verified
        non‑profit corporation, under the DBA{" "}
        <em>The Exchange Alliance</em>. By opting in you agree to these SMS
        Terms in addition to our{" "}
        <a href="/sms/privacy">Privacy Policy</a>.
      </p>

      <h2>1. Program name and description</h2>
      <p>
        <strong>Program Name:</strong> Life Foundation / The Exchange
        Alliance — Case Management Notifications.
      </p>
      <p>
        <strong>Program Description:</strong> A transactional notification
        program that sends opted‑in residents, staff, volunteers, and
        community partners text‑message updates related to their
        case‑management relationship with Life Foundation. Message
        categories include appointment reminders, document and signature
        requests, housing application status updates, case manager
        assignments, training reminders, and operational compliance
        alerts.
      </p>

      <h2>2. How to opt in</h2>
      <p>
        <strong>
          Enrollment in this SMS program is voluntary and is NEVER a
          condition of receiving services from Life Foundation.
        </strong>{" "}
        Residents, staff, and partners may participate in every
        Life Foundation / Exchange Alliance program — housing,
        case management, employment, life skills, training, financial
        assistance — without ever providing a phone number or opting
        in to SMS. SMS is an optional convenience channel offered
        in addition to email, in‑person, and phone outreach.
      </p>
      <p>
        End users who choose to receive SMS may opt in through one of
        the following affirmative methods. In every case the SMS
        consent decision is a separate, distinct decision from any
        program enrollment, housing agreement, employment paperwork,
        or service eligibility determination:
      </p>
      <ul>
        <li>
          <strong>Standalone SMS consent form.</strong> Residents may
          sign a dedicated, single‑purpose SMS consent form (digital or
          paper) that is offered separately from all enrollment,
          intake, lease, and program‑eligibility documents. Declining
          to sign the SMS consent form has no effect on enrollment,
          eligibility, housing, or any other service. The form is
          presented after enrollment is already complete.
        </li>
        <li>
          <strong>Staff account settings.</strong> Employees who wish
          to receive operational SMS may voluntarily enter their
          mobile phone number on the &quot;My Account · SMS&quot; tab inside
          the platform after they are already logged in. The phone
          field is optional; staff who leave it blank or who set
          their SMS toggle to OFF receive zero SMS. Employment is
          not contingent on entering a phone number or enabling
          SMS.
        </li>
        <li>
          <strong>Reply START / YES / UNSTOP.</strong> Previously
          opted‑out users may resume the program at any time by
          texting any of these keywords to our program number.
        </li>
      </ul>
      <p>
        Opt‑in is always affirmative — no checkbox is ever
        pre‑selected on our behalf and no consent is implied by
        signing any other document.
      </p>

      <h2>3. Message frequency and rates</h2>
      <p>
        Message frequency varies based on your role and active cases.
        Residents typically receive between 1 and 8 messages per month;
        staff may receive additional operational alerts.{" "}
        <strong>Message and data rates may apply.</strong> Your wireless
        carrier’s standard rates and fees apply to all text messages, both
        inbound and outbound. Life Foundation is not responsible for
        any carrier charges incurred as part of your participation in the
        program.
      </p>

      <h2>4. Opt‑out, help, and support</h2>
      <p>
        You can opt out of the program at any time:
      </p>
      <ul>
        <li>
          Text <strong>STOP</strong> (also accepted: END, QUIT, CANCEL,
          UNSUBSCRIBE) to any message you receive from us. You will receive
          one confirmation message and no further texts will be sent.
        </li>
        <li>
          Text <strong>HELP</strong> (also accepted: INFO) for support
          information.
        </li>
        <li>
          Email{" "}
          <a href="mailto:will.smith@lifefoundationrebuild.com">will.smith@lifefoundationrebuild.com</a>{" "}
          or call{" "}
          <a href="tel:+18434242655">+1 (843) 424‑2655</a> for direct
          assistance.
        </li>
      </ul>
      <p>
        Standard auto‑responses you will receive:
      </p>
      <ul>
        <li>
          <em>Opt‑in confirmation:</em> “You’re subscribed to Life
          Foundation alerts. Msg &amp; data rates may apply. Msg freq
          varies. Reply HELP for help, STOP to cancel.”
        </li>
        <li>
          <em>HELP reply:</em> “Life Foundation: For assistance, contact
          your case manager or call 843‑424‑2655. Reply STOP to
          unsubscribe.”
        </li>
        <li>
          <em>STOP reply:</em> “You’ve been unsubscribed from Life
          Foundation messages. No further texts will be sent. Reply START
          to resubscribe.”
        </li>
      </ul>

      <h2>5. Supported carriers</h2>
      <p>
        The program is compatible with all major U.S. wireless carriers
        including AT&amp;T, T‑Mobile, Verizon Wireless, US Cellular, Boost,
        Cricket, MetroPCS, Google Fi, and most MVNOs. Service may not be
        available on all carriers at all times.
      </p>

      <h2>6. Carrier non‑liability</h2>
      <p>
        <strong>
          T‑Mobile and other participating wireless carriers are not liable
          for delayed or undelivered messages.
        </strong>{" "}
        Life Foundation is not responsible for the failure of any
        message to be transmitted or received due to causes beyond our
        reasonable control, including but not limited to wireless carrier
        outages, equipment failure, or the recipient’s device being out of
        coverage or powered off.
      </p>

      <h2>7. Eligible recipients</h2>
      <p>
        Participation is limited to U.S. residents age 18 and older (or
        the age of majority in your state) who have an active case
        relationship with Life Foundation or one of its partner
        organizations within The Exchange Alliance. You represent that the
        mobile phone number you have provided is your own or that you are
        authorized to enroll it.
      </p>

      <h2>8. Privacy</h2>
      <p>
        Your participation is governed by our{" "}
        <a href="/sms/privacy">Privacy Policy</a>, which explains what
        information we collect and how it is used. Mobile information will
        not be shared with third parties or affiliates for marketing or
        promotional purposes.
      </p>

      <h2>9. Changes to these Terms</h2>
      <p>
        We may update these SMS Terms from time to time. The “Last
        Updated” date at the top of this page reflects the most recent
        revision. Material changes will be communicated to active
        participants by text message and/or email before they take
        effect.
      </p>

      <h2>10. Governing law</h2>
      <p>
        These SMS Terms are governed by the laws of the State of South
        Carolina, without regard to its conflict‑of‑laws provisions. Any
        disputes will be resolved in the state or federal courts located
        in Horry County, South Carolina.
      </p>

      <h2>11. Contact</h2>
      <p>
        Questions about these SMS Terms or our SMS program:
      </p>
      <p>
        Life Foundation<br />
        7433 Springside Drive, Myrtle Beach, SC 29588<br />
        Email:{" "}
        <a href="mailto:will.smith@lifefoundationrebuild.com">will.smith@lifefoundationrebuild.com</a>
        <br />
        Phone:{" "}
        <a href="tel:+18434242655">+1 (843) 424‑2655</a>
      </p>
    </SmsLegalShell>
  );
}
